/**
 * PaywallUpsellCard — friendly upsell shown instead of a raw error when a
 * test-start request 402s with a paywall code (ROO-82). Shared by
 * TestRunner.tsx and TestTaking.tsx so both surfaces look identical.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PaywallError } from '../../api/billingErrors';
import { isNativeIOS } from '../../utils/platform';

interface PaywallUpsellCardProps {
  paywallError: PaywallError;
  onBack: () => void;
}

const PaywallUpsellCard: React.FC<PaywallUpsellCardProps> = ({ paywallError, onBack }) => {
  const navigate = useNavigate();

  const isFreeLimit = paywallError.code === 'free_limit_reached';
  const limit = paywallError.data?.freeTestsLimit ?? 3;

  const title = isFreeLimit
    ? `You've used your ${limit} free practice tests`
    : 'This is a Premium feature';

  const subtitle = isFreeLimit
    ? 'Start your free 14-day trial for unlimited practice tests — no card required.'
    : 'Start your free 14-day trial to unlock this — no card required.';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: '100%',
          textAlign: 'center',
          background: '#fff',
          border: '1.5px solid #EFE6D0',
          borderRadius: 20,
          padding: '36px 28px',
          fontFamily: 'Lexend, system-ui, sans-serif',
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#FFF3E0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 18px',
          }}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#F58720"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="10" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h2
          style={{
            fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
            fontWeight: 800,
            fontSize: 21,
            color: '#1A2A1F',
            margin: '0 0 8px',
          }}
        >
          {title}
        </h2>
        <p style={{ color: '#6B7A6F', fontSize: 14.5, lineHeight: 1.6, margin: '0 0 24px' }}>
          {subtitle}
        </p>

        {/* Purchase CTA hidden in the iOS shell (App Store 3.1.1 / ROO-235). */}
        {!isNativeIOS() && (
          <button
            type="button"
            onClick={() => navigate('/billing')}
            style={{
              width: '100%',
              padding: '13px 20px',
              borderRadius: 999,
              border: 'none',
              background: '#14593C',
              color: 'white',
              fontWeight: 700,
              fontSize: 15,
              cursor: 'pointer',
              marginBottom: 10,
            }}
          >
            Start free 14-day trial
          </button>
        )}
        <button
          type="button"
          onClick={onBack}
          style={{
            width: '100%',
            padding: '11px 20px',
            borderRadius: 999,
            border: '1.5px solid #E5D9BD',
            background: 'white',
            color: '#14593C',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Back to dashboard
        </button>
      </div>
    </div>
  );
};

export default PaywallUpsellCard;
